import { Ban } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import GenericButton from '../../components/GenericButton';
import GenericDropdown from '../../components/GenericDropDown';
import GenericTable from '../../components/GenericTable';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { GameManagerPermission } from '../../models/GameManagerPermission';
import { Game } from '../../models/Games';
import { User } from '../../models/Users';
import { addManagerGamePermission, getAllGames, getManagerGamePermissions } from '../../redux/entities/gameEntity';
import { getUsersByRole } from '../../redux/entities/userEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { Role } from '../../utils/rolesUtil';

const PageHeading = styled.h2`
  margin-top: 2rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionIcon = styled.div`
  cursor: pointer;
  color: #555;
  font-size: 1.5rem;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }
`;

const ManagerPermissionPage: React.FC = () => {
    const [selectedManager, setSelectedManager] = useState<User | null>(null);
    const [selectedGame, setSelectedGame] = useState<Game | null>(null);
    const dispatch = useAppDispatch();
    const { showModalStrip } = useModalStrip();

    const rawUsers = useAppSelector((state) => state.entities.userReducer.users);
    const users = useMemo(() => (Array.isArray(rawUsers) ? rawUsers : []), [rawUsers]);
    const games = useAppSelector((state) => state.entities.gameReducer.games);
    // Add managerPermissions selector
    const managerPermissions = useAppSelector((state) => state.entities.gameReducer.managerPermissions);

    useEffect(() => {
        if (users.length === 0) {
            dispatch(getUsersByRole(Role.Manager))
                .unwrap()
                .catch((err: Error) => {
                    showModalStrip('danger', err.message, 5000);
                });
        }

        if (games.length === 0) {
            dispatch(getAllGames())
                .unwrap()
                .catch((err: Error) => {
                    showModalStrip('danger', err.message, 5000);
                });
        }

        if (managerPermissions.length === 0) {
            dispatch(getManagerGamePermissions({}))
                .unwrap()
                .catch((err: Error) => {
                    showModalStrip('danger', err.message, 5000);
                });
        }
    }, [dispatch, games.length, managerPermissions.length, showModalStrip, users.length]);

    // Update the handlers
    const handleManagerChange = (managerId: string) => {
        const manager = users.find(user => user.id.toString() === managerId);
        setSelectedManager(manager || null);
    };

    const handleGameChange = (gameName: string) => {
        const game = games.find(g => g.name === gameName);
        setSelectedGame(game || null);
    };

    const columns = [
        {
            header: 'Manager', field: 'managerName', render: (rowData: GameManagerPermission) => {
                return rowData.userDTO.name
            }
        },
        {
            header: 'Game', field: 'gameName', render: (rowData: GameManagerPermission) => {
                return rowData.gameDTO.name
            }
        },
        { header: 'Status', field: 'status' },
        {
            header: 'Actions',
            field: 'actions'
        }
    ];


    const handleAddPermission = () => {
        if (!selectedManager || !selectedGame) {
            showModalStrip('danger', 'Please select all fields', 3000);
            return;
        }

        console.log(selectedManager, selectedGame);
        dispatch(addManagerGamePermission({ manager: selectedManager, game: selectedGame }))
            .unwrap().then(() => {
                showModalStrip('success', 'Permission added successfully', 3000);
                setSelectedManager(null);
                setSelectedGame(null);
            });

        // Dispatch action to add permission
    };

    const handleTogglePermission = (row: GameManagerPermission) => {
        // Dispatch action to toggle permission

        const newStatus = row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        const updatedRow = { ...row, status: newStatus };
        dispatch(addManagerGamePermission({
            manager: updatedRow.userDTO, game: updatedRow.gameDTO,
            id: row.id, status: newStatus
        }))
            .unwrap().then(() => {
                showModalStrip('success', 'Permission updated successfully', 3000);
                setSelectedManager(null);
                setSelectedGame(null);
            });
    };

    const isInactive = (item: any) => {
        return item?.status === 'INACTIVE';  // Assuming 'status' is a field in the data
    }


    return (
        <Container>
            <PageHeading>Manager Permissions</PageHeading>

            <div className="mb-4">
                <GenericDropdown
                    label="Select Manager"
                    options={[
                        ...users
                            .filter(user => user.role === 'Manager')
                            .map(manager => ({
                                value: manager.id.toString(),
                                label: manager.name
                            }))
                    ]}  // Populate with managers
                    onChange={handleManagerChange}
                    value={selectedManager?.id.toString() || ''}
                />

                <GenericDropdown
                    label="Select Game"
                    options={[
                        ...games.map((game) => ({
                            value: game.name,
                            label: game.name,
                        }))
                    ]}  // Populate with games
                    onChange={handleGameChange}
                    value={selectedGame?.name || ''}
                />

                <GenericButton
                    label="Add Permission"
                    onClick={handleAddPermission}
                />
            </div>

            <GenericTable
                columns={columns}
                data={managerPermissions.map((item) => ({
                    ...item,
                    actions: (
                        <ActionButtonContainer>
                            <ActionIcon onClick={() => handleTogglePermission(item)}>
                                <Ban color={isInactive(item) ? '#bb2124' : '#0d4a14'} />
                            </ActionIcon>
                        </ActionButtonContainer>
                    ),
                }))}
                actions={false} // Don't display the generic edit and delete buttons, we are using custom actions
                pagination={true}
            />
        </Container>
    );
};

export default ManagerPermissionPage;